/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  EntityInterface,
  EntitySummaryType,
  TableColCfg
} from '@gfe/core/models/entities/entity-visualization-interface';
import { isNil, isObject } from '@gfe/core/utils/shared-utils';

export class SummaryItem<T> {
  sum: number = 0;
  max: unknown;
  min: unknown;
  private _distinct: Set<string> = new Set();
  private totalNotNull: number = 0;
  readonly summaryTypes: EntitySummaryType[] = [];

  constructor(readonly column: TableColCfg<EntityInterface<T>>) {
    this.summaryTypes = column.summaryTypes ?? [];
  }

  get distinct(): number {
    return this._distinct.size;
  }

  get count(): number {
    return this.totalNotNull;
  }

  get avg(): number {
    if (this.count === 0) return 0;
    return this.sum / this.count;
  }

  addValues(values: EntityInterface<T>[]) {
    values.forEach((value) => this.processValue(value));
  }

  getFormattedSum(): string | undefined {
    if (isNil(this.sum)) return undefined;
    return this.column.renderSummary
      ? String(this.column.renderSummary(this.sum))
      : this.formatNumberValue(this.sum);
  }

  getFormattedMin(): string | undefined {
    if (isNil(this.min)) return undefined;
    return this.column.renderSummary
      ? String(this.column.renderSummary(this.min))
      : this.formatNumberValue(this.min);
  }

  getFormattedMax(): string | undefined {
    if (isNil(this.max)) return undefined;
    return this.column.renderSummary
      ? String(this.column.renderSummary(this.max))
      : this.formatNumberValue(this.max);
  }

  getFormattedAvg(): string | undefined {
    if (isNil(this.avg)) return undefined;
    if (this.column.renderSummary) {
      return String(this.column.renderSummary(this.avg));
    }
    return this.formatNumberValue(this.avg);
  }

  getFormattedValueFor(type: EntitySummaryType): string | undefined {
    switch (type) {
      case 'sum':
        return this.getFormattedSum();
      case 'min':
        return this.getFormattedMin();
      case 'max':
        return this.getFormattedMax();
      case 'avg':
        return this.getFormattedAvg();
      case 'count':
        return String(this.count);
      case 'distinct':
        return String(this.distinct);
      default:
        return undefined;
    }
  }

  private processValue(rawValue: EntityInterface<T>): void {
    const value = this.getValue(rawValue);
    this.processSum(value);
    this.processMax(value);
    this.processMin(value);
    this.processDistinct(value);
    this.processTotalNotNull(value);
  }

  private processSum(value: unknown): void {
    if (!this.summaryTypes.includes('sum')) return;
    if (isNil(value) || isNaN(Number(value))) return;
    this.sum += Number(value);
  }

  private processMax(value: unknown): void {
    if (!this.summaryTypes.includes('max')) return;
    if (isNil(this.max) || (value as any) > (this.max as any)) {
      this.max = value;
    }
  }

  private processMin(value: unknown): void {
    if (!this.summaryTypes.includes('min')) return;
    if (isNil(this.min) || (value as any) < (this.min as any)) {
      this.min = value;
    }
  }

  private processDistinct(value: unknown): void {
    if (!this.summaryTypes.includes('distinct')) return;
    this._distinct.add(String(value));
  }

  private processTotalNotNull(value: unknown): void {
    if (!isNil(value)) {
      this.totalNotNull += 1;
    }
  }

  private formatNumberValue(value: unknown): string {
    if (Number.isInteger(value)) {
      return String(value);
    }
    if (typeof value === 'number') {
      return value.toFixed(1).toString();
    }
    return String(value);
  }

  private getValue(row: EntityInterface<T>): unknown {
    if (this.column.rawValue) {
      return this.column.rawValue(row);
    }

    const keyStr = String(this.column.key);
    const value = keyStr.includes('.')
      ? this.getNestedValue(row, keyStr)
      : (row as any)[keyStr];

    // Mirror the table cell: derived columns (e.g. split columns that read an
    // array by uid) only resolve through their renderHook, so the raw key path
    // alone would be undefined and the value would never be summed.
    return this.column.renderHook ? this.column.renderHook(value, row) : value;
  }

  private getNestedValue(obj: unknown, path: string): unknown {
    if (!obj || !path) return undefined;

    return path.split('.').reduce((current: unknown, key: string) => {
      if (!isObject(current)) return undefined;
      const record = current as Record<PropertyKey, unknown>;
      return record[key];
    }, obj);
  }
}
